import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "../api";
import Layout from "../components/Layout";

import {
  FiTrash2,
  FiEdit,
  FiEye,
  FiPlus,
  FiSave,
  FiClock,
  FiClipboard,
} from "react-icons/fi";
import { toasts } from "../components/AppToast";

// Empty form
const EMPTY_FORM = {
  start_hour: "00",
  start_minute: "00",
  end_hour: "00",
  end_minute: "00",
  hour_charge: "",
};

// Hours
const HOURS = Array.from({ length: 25 }, (_, index) =>
  String(index).padStart(2, "0")
);

// Minutes
const MINUTES = ["00", "01", "15", "30", "45", "59"];

export default function SpecialHourCharge() {
  const navigate = useNavigate();

  // Data
  const [charges, setCharges] = useState([]);
  const [loading, setLoading] = useState(true);

  // Pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Form
  const [form, setForm] = useState({
    ...EMPTY_FORM,
  });

  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Modals
  const [formModal, setFormModal] = useState(false);
  const [viewModal, setViewModal] = useState(null);
  const [delModal, setDelModal] = useState(null);
  const [delSaving, setDelSaving] = useState(false);

  // Auth
  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!token) {
      navigate("/login");
    }
  }, [navigate]);

  // Fetch charges
  const fetchCharges = useCallback(async () => {
    setLoading(true);

    try {
      const response = await API.get("/spacialhourcharge", {
        params: {
          page,
          limit,
        },
      });

      const data = response.data || {};

      setCharges(Array.isArray(data.data) ? data.data : []);
      setTotal(Number(data.total || 0));
      setTotalPages(Number(data.totalPages || 0));
    } catch (error) {
      console.error("fetchCharges error:", error);

      toasts.error(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to load hour charges"
      );

      setCharges([]);
      setTotal(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [page, limit]);

  // Fetch data when page changes
  useEffect(() => {
    fetchCharges();
  }, [fetchCharges]);

  // Reset form
  const resetForm = () => {
    setForm({
      ...EMPTY_FORM,
    });

    setEditId(null);
  };

  // Open add modal
  const openAddModal = () => {
    resetForm();
    setFormModal(true);
  };

  // Close form modal
  const closeFormModal = () => {
    if (saving) {
      return;
    }

    setFormModal(false);
    resetForm();
  };

  // Handle form change
  const handleChange = (field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  // Format time
  const formatTime = (time) => {
    if (time === undefined || time === null || time === "") {
      return "—";
    }

    return String(time).substring(0, 5);
  };

  // Get start time
  const getStartTime = (item) => {
    return formatTime(item?.start_time);
  };

  // Get end time
  const getEndTime = (item) => {
    return formatTime(item?.end_time);
  };

  // Get hour charge
  const getCharge = (item) => {
    return item?.hour_chrge ?? "0";
  };

  // Format charge
  const formatCharge = (item) => {
    const charge = getCharge(item);
    const number = Number(charge);

    if (Number.isNaN(number)) {
      return charge;
    }

    return number.toFixed(2);
  };

  // Get ID
  const getId = (item) => {
    return item?.splhour_id ?? null;
  };

  // Convert time to form
  const timeToForm = (time) => {
    if (!time || !String(time).includes(":")) {
      return {
        hour: "00",
        minute: "00",
      };
    }

    const parts = String(time).split(":");

    return {
      hour: String(parts[0]).padStart(2, "0"),
      minute: String(parts[1]).padStart(2, "0"),
    };
  };

  // Edit record
  const startEdit = (item) => {
    try {
      const start = timeToForm(item?.start_time);
      const end = timeToForm(item?.end_time);
      const charge = getCharge(item);

      setForm({
        start_hour: start.hour,
        start_minute: start.minute,
        end_hour: end.hour,
        end_minute: end.minute,
        hour_charge:
          charge === null || charge === undefined ? "" : String(charge),
      });

      setEditId(getId(item));
      setFormModal(true);
    } catch (error) {
      console.error("startEdit error:", error);

      toasts.error("Failed to load record");
    }
  };

  // View record
  const handleView = (item) => {
    setViewModal(item);
  };

  // Validate form
  const validateForm = () => {
    if (
      form.start_hour === "" ||
      form.start_minute === "" ||
      form.end_hour === "" ||
      form.end_minute === ""
    ) {
      toasts.error("Start Time and End Time are required");
      return false;
    }

    if (
      form.hour_charge === "" ||
      form.hour_charge === null ||
      form.hour_charge === undefined
    ) {
      toasts.error("Hour Charge is required");
      return false;
    }

    const charge = Number(form.hour_charge);

    if (Number.isNaN(charge)) {
      toasts.error("Hour Charge must be a valid number");
      return false;
    }

    if (charge < 0) {
      toasts.error("Hour Charge cannot be negative");
      return false;
    }

    return true;
  };

  // Submit form
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setSaving(true);

    try {
      const payload = {
        start_hour: Number(form.start_hour),
        start_minute: Number(form.start_minute),
        end_hour: Number(form.end_hour),
        end_minute: Number(form.end_minute),
        hour_charge: Number(form.hour_charge),
      };

      if (editId !== null) {
        await API.put(`/spacialhourcharge/${editId}`, payload);

        toasts.success("Hour charge updated successfully");
      } else {
        await API.post("/spacialhourcharge", payload);

        toasts.success("Hour charge added successfully");
      }

      setFormModal(false);
      resetForm();

      await fetchCharges();
    } catch (error) {
      console.error("Submit error:", error);

      toasts.error(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Operation failed"
      );
    } finally {
      setSaving(false);
    }
  };

  // Delete record
  const handleDelete = async () => {
    if (!delModal) {
      return;
    }

    const id = getId(delModal);

    if (id === null || id === undefined) {
      toasts.error("Record ID not found");
      return;
    }

    setDelSaving(true);

    try {
      await API.delete(`/spacialhourcharge/${id}`);

      toasts.success("Hour charge deleted successfully");

      setDelModal(null);

      if (charges.length === 1 && page > 1) {
        setPage((previous) => Math.max(1, previous - 1));
      } else {
        await fetchCharges();
      }
    } catch (error) {
      console.error("Delete error:", error);

      toasts.error(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Delete failed"
      );
    } finally {
      setDelSaving(false);
    }
  };

  return (
    <Layout activePage="Special Hour Charge">
      <main className="dashboard-main">
        <div className="table-card">
          {/* Header */}
          <div className="table-card-head">
            <h3 className="table-card-title d-flex align-items-center">
              <FiClipboard size={26} className="me-2" />
              Manage Rent Durations
            </h3>

            <button
              type="button"
              className="um-btn-primary"
              onClick={openAddModal}
            >
              <span className="um-add-icon">＋</span>
              Add Hour Charge
            </button>
          </div>

          {/* Table */}
          <div className="table-responsive">
            <table className="um-table">
              <thead>
                <tr className="um-table-head">
                  <th className="um-th text-center">#</th>
                  <th className="um-th text-center">Start Time</th>
                  <th className="um-th text-center">End Time</th>
                  <th className="um-th text-center">Hour Charge</th>
                  <th className="um-th text-center">Action</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, index) => (
                    <tr key={index}>
                      {Array.from({ length: 5 }).map((_, cellIndex) => (
                        <td key={cellIndex} className="um-td text-center">
                          <div className="skeleton" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : charges.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="empty-table-cell text-center py-5"
                    >
                      <div className="empty-icon">
                        <FiClock />
                      </div>

                      <div className="empty-title">
                        No hour charges found
                      </div>

                      <div className="empty-sub">
                        Use the Add Hour Charge button to add one
                      </div>
                    </td>
                  </tr>
                ) : (
                  charges.map((item, index) => (
                    <tr
                      key={getId(item) ?? index}
                      className={`um-row ${
                        index % 2 === 0 ? "even-row" : "odd-row"
                      }`}
                    >
                      <td className="um-td text-center">
                        {(page - 1) * limit + index + 1}
                      </td>

                      <td className="um-td text-center">
                        {getStartTime(item)}
                      </td>

                      <td className="um-td text-center">
                        {getEndTime(item)}
                      </td>

                      <td className="um-td text-center">
                        <span className="badge badge-success">
                          ₹ {formatCharge(item)}
                        </span>
                      </td>

                      <td className="um-td text-center">
                        <div className="action-buttons">
                          <button
                            type="button"
                            className="action-btn action-view"
                            title="View"
                            onClick={() => handleView(item)}
                          >
                            <FiEye size={15} />
                          </button>

                          <button
                            type="button"
                            className="action-btn action-edit"
                            title="Edit"
                            onClick={() => startEdit(item)}
                          >
                            <FiEdit size={15} />
                          </button>

                          <button
                            type="button"
                            className="action-btn action-delete delete-enabled"
                            title="Delete"
                            onClick={() => setDelModal(item)}
                          >
                            <FiTrash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 0 && (
              <div className="pagination">
                <span className="pagin-info">
                  Showing {(page - 1) * limit + 1}
                  {" – "}
                  {Math.min(page * limit, total)} of {total}
                </span>

                <div className="d-flex align-items-center gap-2">
                  <button
                    type="button"
                    className="pg-btn"
                    disabled={page === 1 || loading}
                    onClick={() =>
                      setPage((previous) => Math.max(1, previous - 1))
                    }
                  >
                    ← Prev
                  </button>

                  <span className="pagin-info">
                    Page {page} / {totalPages}
                  </span>

                  <button
                    type="button"
                    className="pg-btn"
                    disabled={page >= totalPages || loading}
                    onClick={() =>
                      setPage((previous) =>
                        Math.min(totalPages, previous + 1)
                      )
                    }
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Add / Edit Modal */}
        {formModal && (
          <div className="um-overlay" onClick={closeFormModal}>
            <div
              className="um-modal um-view-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="um-modal-head">
                <div className="d-flex align-items-center gap-2">
                  <div className="form-card-icon">
                    {editId !== null ? (
                      <FiEdit size={24} />
                    ) : (
                      <FiPlus size={24} />
                    )}
                  </div>

                  <div>
                    <h2 className="um-modal-title mb-0">
                      {editId !== null
                        ? "Edit Hour Charge"
                        : "Add Hour Charge"}
                    </h2>

                    <p className="form-card-sub mb-0">
                      Configure start time, end time and hourly charge
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  className="um-x-btn"
                  onClick={closeFormModal}
                  disabled={saving}
                >
                  ✕
                </button>
              </div>

              <div className="um-modal-scroll">
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="field-label">Start Time *</label>

                    <div className="d-flex gap-2">
                      <select
                        className="rd-input"
                        value={form.start_hour}
                        onChange={(e) =>
                          handleChange("start_hour", e.target.value)
                        }
                      >
                        {HOURS.map((hour) => (
                          <option key={hour} value={hour}>
                            {hour}
                          </option>
                        ))}
                      </select>

                      <select
                        className="rd-input"
                        value={form.start_minute}
                        onChange={(e) =>
                          handleChange("start_minute", e.target.value)
                        }
                      >
                        {MINUTES.map((minute) => (
                          <option key={minute} value={minute}>
                            {minute}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="col-md-4">
                    <label className="field-label">End Time *</label>

                    <div className="d-flex gap-2">
                      <select
                        className="rd-input"
                        value={form.end_hour}
                        onChange={(e) =>
                          handleChange("end_hour", e.target.value)
                        }
                      >
                        {HOURS.map((hour) => (
                          <option key={hour} value={hour}>
                            {hour}
                          </option>
                        ))}
                      </select>

                      <select
                        className="rd-input"
                        value={form.end_minute}
                        onChange={(e) =>
                          handleChange("end_minute", e.target.value)
                        }
                      >
                        {MINUTES.map((minute) => (
                          <option key={minute} value={minute}>
                            {minute}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="col-md-4">
                    <label className="field-label">Hour Charge *</label>

                    <input
                      name="hour_charge"
                      id="hour_charge"
                      className="rd-input"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Enter hour charge"
                      value={form.hour_charge}
                      onChange={(e) =>
                        handleChange("hour_charge", e.target.value)
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="um-modal-foot">
                <button
                  type="button"
                  className="um-btn-secondary"
                  onClick={closeFormModal}
                  disabled={saving}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="um-btn-primary"
                  onClick={handleSubmit}
                  disabled={saving}
                >
                  {saving ? (
                    <span className="spinner" />
                  ) : editId !== null ? (
                    <>
                      <FiSave />
                      Update Hour Charge
                    </>
                  ) : (
                    <>
                      <FiPlus />
                      Add Hour Charge
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* View Modal */}
        {viewModal && (
          <div className="um-overlay" onClick={() => setViewModal(null)}>
            <div
              className="um-modal um-view-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="um-modal-head">
                <div>
                  <h2 className="um-modal-title mb-0">
                    <FiClock /> Hour Charge Details
                  </h2>

                  <p className="form-card-sub mb-0">
                    Special hour charge configuration
                  </p>
                </div>

                <button
                  type="button"
                  className="um-x-btn"
                  onClick={() => setViewModal(null)}
                >
                  ✕
                </button>
              </div>

              <div className="um-modal-scroll">
                <div className="row g-3">
                  <div className="col-md-4">
                    <div className="bg-light rounded-3 border p-3">
                      <div className="small text-muted text-uppercase fw-semibold mb-1">
                        Start Time
                      </div>

                      <div className="fw-bold">
                        {getStartTime(viewModal)}
                      </div>
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="bg-light rounded-3 border p-3">
                      <div className="small text-muted text-uppercase fw-semibold mb-1">
                        End Time
                      </div>

                      <div className="fw-bold">{getEndTime(viewModal)}</div>
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="bg-light rounded-3 border p-3">
                      <div className="small text-muted text-uppercase fw-semibold mb-1">
                        Hour Charge
                      </div>

                      <div className="fw-bold text-success">
                        ₹ {formatCharge(viewModal)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="um-modal-foot">
                <button
                  type="button"
                  className="um-btn-secondary"
                  onClick={() => setViewModal(null)}
                >
                  Close
                </button>

                <button
                  type="button"
                  className="um-btn-primary"
                  onClick={() => {
                    const data = viewModal;

                    setViewModal(null);

                    setTimeout(() => {
                      startEdit(data);
                    }, 100);
                  }}
                >
                  <FiEdit />
                  Edit Hour Charge
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Modal */}
        {delModal && (
          <div className="um-overlay" onClick={() => setDelModal(null)}>
            <div
              className="um-modal um-delete-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="delete-content">
                <div className="delete-icon">
                  <FiTrash2 />
                </div>

                <h2 className="delete-title">Delete Hour Charge?</h2>

                <div className="delete-badges">
                  <span className="badge badge-success">
                    {getStartTime(delModal)}
                  </span>

                  &nbsp;

                  <span className="badge badge-success">
                    {getEndTime(delModal)}
                  </span>

                  &nbsp;

                  <span className="badge bg-warning text-dark">
                    ₹ {formatCharge(delModal)}
                  </span>
                </div>

                <p className="delete-description">
                  This hour charge configuration will be permanently deleted.
                  <br />
                  <strong>This action cannot be undone.</strong>
                </p>

                <div className="delete-actions">
                  <button
                    type="button"
                    className="um-btn-secondary"
                    onClick={() => setDelModal(null)}
                    disabled={delSaving}
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    disabled={delSaving}
                    className="delete-confirm-btn"
                    onClick={handleDelete}
                  >
                    {delSaving ? (
                      <span className="spinner" />
                    ) : (
                      <>
                        <FiTrash2 size={16} />
                        Delete Permanently
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

     
      </main>
    </Layout>
  );
}
